import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	clampPos,
	clampSize,
	defaultPos,
	maxX,
	maxY,
	resizeRect,
	snapX,
	DEFAULT_SIZE,
	SNAP_MS,
	TOP_INSET,
	type Corner,
	type Pos,
	type Size,
	type Viewport
} from './floatingChatGeometry';

const POS_KEY = 'floating-chat-pos';
const SIZE_KEY = 'floating-chat-size';
// Weight of the newest sample in the velocity EMA (rest is carried history) — balances jitter
// rejection against responsiveness when deciding whether a release was a flick.
const VELOCITY_SMOOTHING = 0.6;

const viewport = (): Viewport => ({ width: window.innerWidth, height: window.innerHeight });

const isSize = (v: unknown): v is Size =>
	typeof v === 'object' &&
	v !== null &&
	typeof (v as Size).width === 'number' &&
	typeof (v as Size).height === 'number';
const isPos = (v: unknown): v is Pos =>
	typeof v === 'object' && v !== null && typeof (v as Pos).x === 'number' && typeof (v as Pos).y === 'number';

const readStored = <T>(key: string, isValid: (v: unknown) => v is T): T | null => {
	try {
		const raw = window.localStorage.getItem(key);
		if (raw) {
			const parsed: unknown = JSON.parse(raw);
			if (isValid(parsed)) return parsed;
		}
	} catch {
		// localStorage may be unavailable (private mode); fall back to defaults.
	}
	return null;
};

const persist = (key: string, value: unknown): void => {
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// localStorage may be unavailable (private mode); the value simply won't persist.
	}
};

// Owns the floating chat window's position and size: drag-to-move (with fling-to-edge snapping),
// corner resize, viewport re-clamping, and localStorage persistence. The component keeps the
// chat content concerns; this hook keeps the window-chrome geometry.
export const useFloatingChatWindow = () => {
	const [size, setSize] = useState<Size>(() => {
		const stored = readStored(SIZE_KEY, isSize);
		return clampSize(stored ?? DEFAULT_SIZE, viewport());
	});

	const [pos, setPos] = useState<Pos>(() => {
		const stored = readStored(POS_KEY, isPos);
		return stored ? clampPos(stored, size, viewport()) : defaultPos(size, viewport());
	});

	// Latest pos/size for the window-level handlers, which capture their closure once and would
	// otherwise read stale state.
	const posRef = useRef(pos);
	const sizeRef = useRef(size);
	posRef.current = pos;
	sizeRef.current = size;

	useEffect(() => persist(POS_KEY, pos), [pos]);
	useEffect(() => persist(SIZE_KEY, size), [size]);

	// Re-clamp size and position into view when the viewport shrinks, so the window can't get
	// stranded off-screen or grow past the edges.
	useEffect(() => {
		const onResize = () => {
			const vp = viewport();
			const nextSize = clampSize(sizeRef.current, vp);
			setSize(nextSize);
			setPos(clampPos(posRef.current, nextSize, vp));
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	// Fling-to-snap: track horizontal pointer speed during the drag and, on release, dock the
	// window flush to the left/right edge if the flick was fast enough. A slow release just drops
	// the window where it lies.
	const velocityX = useRef(0);
	const lastDrag = useRef<{ x: number; t: number } | null>(null);
	const snapTimer = useRef<number>();
	const [snapping, setSnapping] = useState(false);

	// Cancel a pending dock cleanup if the window unmounts mid-animation.
	useEffect(() => () => window.clearTimeout(snapTimer.current), []);

	const onDragStart = useCallback(() => {
		velocityX.current = 0;
		lastDrag.current = { x: posRef.current.x, t: performance.now() };
		setSnapping(false);
	}, []);

	const onDrag = useCallback((_e: unknown, data: { x: number }) => {
		const t = performance.now();
		const prev = lastDrag.current;
		if (prev && t > prev.t) {
			const sample = (data.x - prev.x) / (t - prev.t);
			// EMA so a single jittery final sample doesn't dominate the decision.
			velocityX.current = velocityX.current * (1 - VELOCITY_SMOOTHING) + sample * VELOCITY_SMOOTHING;
		}
		lastDrag.current = { x: data.x, t };
	}, []);

	const onDragStop = useCallback((_e: unknown, data: { x: number; y: number }) => {
		const vp = viewport();
		const target = snapX(velocityX.current, sizeRef.current, vp);
		if (target === null) {
			setPos({ x: data.x, y: data.y });
			return;
		}
		setSnapping(true);
		setPos(clampPos({ x: target, y: data.y }, sizeRef.current, vp));
		window.clearTimeout(snapTimer.current);
		snapTimer.current = window.setTimeout(() => setSnapping(false), SNAP_MS);
	}, []);

	// Corner resize via pointer capture. The handles aren't the drag handle, so react-draggable
	// ignores them; we drive width/height (and x/y for the top/left corners) ourselves.
	const resizeStart = useRef<{ corner: Corner; px: number; py: number; pos: Pos; size: Size } | null>(null);

	const resizeHandleProps = useCallback((corner: Corner) => {
		// Shared by pointer-up and pointer-cancel: an interrupted gesture (touch/pen) must clear
		// the in-progress state, or a later hover-move would resize with no button held.
		const endResize = (e: React.PointerEvent) => {
			if (!resizeStart.current) return;
			resizeStart.current = null;
			if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
		};
		return {
			onPointerDown: (e: React.PointerEvent) => {
				e.preventDefault();
				e.stopPropagation();
				e.currentTarget.setPointerCapture(e.pointerId);
				resizeStart.current = {
					corner,
					px: e.clientX,
					py: e.clientY,
					pos: posRef.current,
					size: sizeRef.current
				};
			},
			onPointerMove: (e: React.PointerEvent) => {
				const start = resizeStart.current;
				if (!start) return;
				const next = resizeRect(
					start.corner,
					{ pos: start.pos, size: start.size },
					e.clientX - start.px,
					e.clientY - start.py,
					viewport()
				);
				setSize(next.size);
				setPos(next.pos);
			},
			onPointerUp: endResize,
			onPointerCancel: endResize
		};
	}, []);

	const vp = viewport();
	const bounds = { left: 0, top: TOP_INSET, right: maxX(size, vp), bottom: maxY(size, vp) };

	return { pos, size, snapping, bounds, onDragStart, onDrag, onDragStop, resizeHandleProps };
};
